import React, { useEffect, useRef, useState } from 'react';
import Tab, { TabProps } from '../tab/Tab';
import Icon from '../../common/components/Icon';

export interface TabsProps {
  tabs: TabProps[];
  fontSize?: 14 | 16;
  useHashRouting?: boolean;
  hasBottomBorder?: boolean;
  onTabClick?: (link: string) => void;
  fullWidth?: boolean;
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  fontSize = 16,
  useHashRouting = false,
  hasBottomBorder = false,
  onTabClick,
  fullWidth = true,
}) => {
  const [location, setLocation] = useState(useHashRouting ? window.location.hash : window.location.hash);

  // ----- overflow logic ----->
  const [isOverflowLeft, setIsOverflowLeft] = useState(false);
  const [isOverflowRight, setIsOverflowRight] = useState(false);
  const tabsListRef = useRef<HTMLUListElement | null>(null);

  const checkForOverflow = () => {
    const tabsListEl = tabsListRef?.current;

    if (!tabsListEl) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabsListEl;

    setIsOverflowLeft(scrollLeft > 0);
    setIsOverflowRight(scrollWidth - scrollLeft > clientWidth);
  };

  useEffect(() => {
    const tabsListEl = tabsListRef?.current;
    if (!tabsListEl) return;

    tabsListEl.addEventListener('scroll', checkForOverflow);
    window.addEventListener('resize', checkForOverflow);

    checkForOverflow();

    return () => {
      tabsListEl.removeEventListener('scroll', checkForOverflow);
      window.removeEventListener('resize', checkForOverflow);
    };
  }, []);
  // <----- overflow logic -----

  useEffect(() => {
    const handleLocationChange = () => setLocation(useHashRouting ? window.location.hash : window.location.pathname);

    const eventType = useHashRouting ? 'hashchange' : 'popstate';

    window.addEventListener(eventType, handleLocationChange);

    return () => window.removeEventListener(eventType, handleLocationChange);
  }, [useHashRouting]);

  const handleTabClick = (link: string) => {
    if (useHashRouting) {
      window.location.hash = link;
    } else {
      onTabClick?.(link);
    }
  };

  const tabsClassName = `tabs ${isOverflowLeft ? 'tabs--overflow-left' : ''} ${isOverflowRight ? 'tabs--overflow-right' : ''}`;

  const detectActiveTab = (tab: TabProps, index: number) => {
    if (useHashRouting) {
      return (
        location.includes(tab.link as string) || (index === 0 && !tabs.some((t) => location.includes(t.link as string)))
      );
    } else {
      const url = new URL(window.location.href);
      const queryParam = tab?.link?.split('=')[0];
      const queryValue = tab?.link?.split('=')[1];
      const currentValue = url.searchParams.get(queryParam || '');
      return currentValue === queryValue || (currentValue === null && index === 0);
    }
  };

  return (
    <div className="tabs-wrapper">
      <div className={tabsClassName}>
        <button
          className={`tabs__scroll-button tabs__scroll-button--left ${isOverflowLeft ? 'visible' : ''} ${hasBottomBorder ? 'tabs__scroll-button--bottom-border' : ''}`}
          onClick={() => tabsListRef.current?.scrollBy({ left: -100, behavior: 'smooth' })}
        >
          <Icon name="chevron-left" />
        </button>
        <ul className={`tabs__list ${hasBottomBorder ? 'tabs__list--has-border' : ''} `} ref={tabsListRef}>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              link={tab.link}
              isActive={detectActiveTab(tab, index)}
              icon={tab.icon}
              fontSize={fontSize}
              onClick={() => handleTabClick(tab.link || '')}
              hasDividerOnRight={tab.hasDividerOnRight}
            />
          ))}
        </ul>
        <button
          className={`tabs__scroll-button tabs__scroll-button--right ${isOverflowRight ? 'visible' : ''}`}
          onClick={() => tabsListRef.current?.scrollBy({ left: 100, behavior: 'smooth' })}
        >
          <Icon name="chevron-right" />
        </button>
      </div>

      {hasBottomBorder && <div className={`tabs__divider ${fullWidth ? '' : 'not-full-width'}`} />}
    </div>
  );
};

export default Tabs;
